import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, Clock, Trash2 } from "lucide-react";
import { clearRecents } from "./recents";
import type { SelectOption } from "./types";

interface ResponsiveCommandSelectListProps {
  inputRef: React.Ref<HTMLInputElement>;
  searchPlaceholder: string;
  effectiveSearch: string;
  onSearchChange: (value: string) => void;
  isSearching: boolean;
  hasRecents: boolean;
  recentOptions: SelectOption[];
  recentValueSet: Set<string>;
  options: SelectOption[];
  value: string;
  onSelect: (id: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  enableRecents: boolean;
  recentKey?: string;
  onClearRecents: () => void;
  customOptionRenderer?: (option: SelectOption) => React.ReactNode;
}

export function ResponsiveCommandSelectList({
  inputRef,
  searchPlaceholder,
  effectiveSearch,
  onSearchChange,
  isSearching,
  hasRecents,
  recentOptions,
  recentValueSet,
  options,
  value,
  onSelect,
  onLoadMore,
  hasMore,
  isLoadingMore,
  enableRecents,
  recentKey,
  onClearRecents,
  customOptionRenderer,
}: ResponsiveCommandSelectListProps) {
  return (
    <Command shouldFilter={false} className="w-full bg-transparent rounded-none shadow-none">
      <CommandInput
        ref={inputRef}
        placeholder={searchPlaceholder}
        value={effectiveSearch}
        onValueChange={onSearchChange}
        autoFocus
      />
      <CommandList
        className="w-full max-h-[260px] overflow-y-auto"
        onScroll={(e: React.UIEvent<HTMLDivElement>) => {
          const el = e.currentTarget as HTMLDivElement;
          const threshold = 64; // px from bottom
          if (
            onLoadMore &&
            hasMore &&
            !isLoadingMore &&
            el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
          ) {
            onLoadMore();
          }
        }}
      >
        <CommandEmpty>{isSearching ? "Searching..." : "No results found."}</CommandEmpty>
        {hasRecents && (
          <CommandGroup
            className="w-full"
            heading={
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-4 w-4" /> Recent
              </div>
            }
          >
            {recentOptions.map((opt) => (
              <CommandItem
                className="w-full"
                key={`recent-${opt.value}`}
                value={opt.value}
                onSelect={onSelect}
                keywords={[opt.label, ...(opt.searchableText?.split(" ") || [])]}
              >
                <div className="flex items-center w-full">
                  <div className="flex-1">
                    {customOptionRenderer ? (
                      customOptionRenderer(opt)
                    ) : (
                      <>
                        <div>{opt.label}</div>
                        {opt.description && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {opt.description}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {opt.value === value && <Check className="ml-2 h-4 w-4" />}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup className="w-full">
          {options
            .filter((opt) => !recentValueSet.has(opt.value))
            .filter((opt) => {
              const s = (effectiveSearch || "").toLowerCase();
              if (!s) return true;
              const hay = `${opt.label} ${opt.searchableText || ""}`.toLowerCase();
              return hay.includes(s);
            })
            .map((opt) => (
              <CommandItem
                className="w-full"
                key={opt.value}
                value={opt.value}
                onSelect={onSelect}
                keywords={[opt.label, ...(opt.searchableText?.split(" ") || [])]}
              >
                <div className="flex items-center w-full">
                  <div className="flex-1">
                    {customOptionRenderer ? (
                      customOptionRenderer(opt)
                    ) : (
                      <>
                        <div>{opt.label}</div>
                        {opt.description && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {opt.description}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {opt.value === value && <Check className="ml-2 h-4 w-4" />}
                </div>
              </CommandItem>
            ))}
        </CommandGroup>
        {onLoadMore && (
          <div className="w-full py-2 text-center text-xs text-muted-foreground">
            {isLoadingMore ? "Loading more..." : hasMore ? "Scroll for more" : "End of results"}
          </div>
        )}
        {enableRecents && recentKey && (
          <div className="flex items-center justify-end p-2">
            <Button
              type="button"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  clearRecents(recentKey);
                } catch {}
                onClearRecents();
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear recents
            </Button>
          </div>
        )}
      </CommandList>
    </Command>
  );
}
